import React from 'react';
import AnchorCoverTag from '../../StoreHomePage/AnchorCoverTag';
import { YotubrVideoFrame } from '../../StoreHomePage/YoutubeVideoFrame';
import { DueDateContent } from './styled';

export default function index({ stopRedirection, showVideo }) {
  const commonProps = { stopRedirection };
  return (
    <DueDateContent className={stopRedirection ? 'no_highlight' : 'showLinks'}>
      <div>
        <h2>What is a Due Date Calculator? </h2>
        <p>
          A due date or pregnancy calculator is an online tool that helps you
          estimate the date of delivery for your pregnancy. It takes into
          consideration the date of your{' '}
          <AnchorCoverTag
            url="/article/difference-in-lmp-and-ultrasound-scan-192312"
            {...commonProps}
          >
            last menstrual period
          </AnchorCoverTag>{' '}
          and the average length of your menstrual cycle to calculate your
          estimated due date (EDD).
        </p>
        <p>
          A due date predictor can be helpful in planning for the arrival of
          your baby and{' '}
          <AnchorCoverTag url="/parenting/pregnancy" {...commonProps}>
            keeping track of your pregnancy milestones.
          </AnchorCoverTag>{' '}
          However, it is important to note that the EDD is only an estimate and
          your baby may arrive before or after the calculated date.
        </p>
      </div>

      <div>
        <h2>How is Your Due Date Calculated? </h2>
        <p>
          A due date predictor is usually based on the first day of the
          woman&apos;s last menstrual period and the average length of her
          menstrual cycle. This is known as the Naegele&apos;s rule. The due
          date is estimated to be 40 weeks or 280 days from the first day of the
          last menstrual period.{' '}
        </p>
        <p>
          However, this method of calculating the EDD assumes that the woman has
          a regular menstrual cycle of 28 days and ovulates on day 14. In
          reality, not all women have a regular menstrual cycle, and{' '}
          <AnchorCoverTag
            url="/article/ovulation-period-the-most-fertile-window-open-the-roadblock-to-pregnancy-147934"
            {...commonProps}
          >
            ovulation
          </AnchorCoverTag>{' '}
          can occur at different times.{' '}
        </p>
        <p>
          Alternatively, healthcare providers can use ultrasound scan to
          estimate the gestational age of the fetus and calculate the EDD. This
          method is more accurate and can be used to adjust the due date if
          there is a discrepancy between the EDD based on the last menstrual
          period and the{' '}
          <AnchorCoverTag
            url={'/article/how-to-read-ultrasound-report-of-pregnancy-167715'}
            {...commonProps}
          >
            ultrasound measurement.
          </AnchorCoverTag>{' '}
        </p>
        <p>
          It&apos;s important to remember that the due date is only an estimate
          and the actual date of delivery can vary. Only about 5% of babies are
          born on their due dates. Your healthcare provider will monitor your
          pregnancy and make adjustments to your EDD if necessary.{' '}
        </p>
      </div>

      <div>
        <h2>Is the pregnancy calculator always accurate? </h2>
        <p>
          Due date calculator is not always accurate because it is based on
          estimates and assumptions. The actual date of delivery can vary
          depending on various factors such as the length of the menstrual
          cycle, ovulation, and the{' '}
          <AnchorCoverTag
            url="/article/fetal-growth-and-development-during-pregnancy-200178"
            {...commonProps}
          >
            growth rate of the fetus.
          </AnchorCoverTag>{' '}
        </p>
        <p>
          The due date predictor assumes that a woman has a regular menstrual
          cycle of 28 days and ovulates on day 14, which may not always be the
          case. Additionally, ultrasound measurements used to estimate the
          gestational age of the fetus can also have some degree of error.{' '}
        </p>
        <p>
          It&apos;s important to remember that the due date is just an estimate,
          and your healthcare provider will monitor your pregnancy to ensure
          that everything is progressing as expected. If there is any concern
          about the growth or development of the fetus, your healthcare provider
          may change{' '}
          <AnchorCoverTag
            url="/article/know-why-your-doctor-may-change-your-due-date-duri-25696"
            {...commonProps}
          >
            the due date accordingly.
          </AnchorCoverTag>{' '}
          Ultimately, the actual date of delivery can depend on many factors,
          and the pregnancy calculator should be used as a general guide rather
          than an exact prediction.
        </p>
      </div>

      <div>
        {' '}
        <h2>What are some possible due date calculation errors? </h2>
        <p>
          There are several possible errors that can occur when calculating an
          EDD. Here are some of the most common:
        </p>
        <ul>
          <li>
            Using an inaccurate last menstrual period (LMP) date: If a woman is
            unsure of her LMP date or has{' '}
            <AnchorCoverTag
              url="/article/how-to-determine-pregnancy-due-date-with-irregular-periods-156098"
              {...commonProps}
            >
              irregular periods
            </AnchorCoverTag>{' '}
            , this can lead to an inaccurate due date calculation.{' '}
          </li>
          <li>
            Assuming a 28-day menstrual cycle: While a 28-day cycle is average,
            not all women have the same cycle length. Using an average cycle
            length can lead to an inaccurate due date calculation.{' '}
          </li>
          <li>
            Variations in ovulation: Ovulation can occur at different times in a
            woman&apos;s cycle, which can affect the EDD.
          </li>
          <li>
            Human error: Errors can occur when entering data into the
            calculation, such as mistyping a date in the due date calculator or
            miscalculating the number of days.{' '}
          </li>
          <li>
            Fetal growth: Not all babies grow at the same rate, and this can
            affect the EDD.{' '}
          </li>
          <li>
            Ultrasound accuracy: While ultrasound measurements are generally
            more accurate than LMP calculations, they are not infallible and can
            also have a degree of error.{' '}
          </li>
        </ul>
        <p>
          It&apos;s important to remember that due dates are estimated and can
          be adjusted as needed throughout pregnancy based on various factors.
          Your healthcare provider will monitor your pregnancy to ensure that
          everything is progressing as expected.{' '}
        </p>
      </div>

      <div>
        <h3>Conception Date </h3>
        <p>
          If you’ve been{' '}
          <AnchorCoverTag
            url="/article/what-are-the-signs-of-ovulation-how-to-estimate-when-youre-ovulating-to-boost-chances-of-pregnancy-34718"
            {...commonProps}
          >
            tracking ovulation symptoms
          </AnchorCoverTag>{' '}
          or using an ovulation calculator, you can also use your conception
          date to see how far along you’re and when your baby is most likely to
          arrive. You can calculate your due date just by adding 266 days to the
          conception date.{' '}
        </p>
      </div>

      <div>
        <h3> IVF Transfer Date </h3>
        <p>
          If you’ve conceived through{' '}
          <AnchorCoverTag
            url="/article/a-step-by-step-view-of-the-process-of-in-vitro-fertilization-ivf-what-should-you-expect-34781"
            {...commonProps}
          >
            in vitro fertilization (IVF)
          </AnchorCoverTag>{' '}
          vitro fertilization (IVF), you can calculate your due date with the
          help of your IVF transfer date. Most embryo transfers take place 3-5
          days after egg retrieval and fertilization
        </p>
        <p>
          You can calculate your due delivery date by adding 263 days to your
          IVF transfer date if you had a day 3 transfer or by adding 261 days
          for day 5 transfer.{' '}
        </p>
      </div>

      <div>
        <h2> Can I Plan My Due Date? </h2>
        <p>
          Whether you’re trying to avoid welcoming your baby in the peak of
          winter or hoping to extend your maternity leave into summer vacation,
          you can try to{' '}
          <AnchorCoverTag
            url="/article/everything-you-need-to-know-about-conception-cycle-182913"
            {...commonProps}
          >
            time your date of conception
          </AnchorCoverTag>{' '}
          in order to plan your due date.
        </p>
        <p>
          But remember, even the best laid plans have a way of going awry. So,
          even if you are one of the lucky ones who are able to plan their due
          date, your baby may decide to come early or late.{' '}
        </p>
        <p>
          Still, to increase your chances of conception and delivery date
          planning, you can try our{' '}
          <AnchorCoverTag
            url="/tools/ovulation-calculator-fertility-calendar"
            {...commonProps}
          >
            Ovulation Calculator
          </AnchorCoverTag>{' '}
          . An ovulation calculator uses the date of your last menstrual period
          and your usual cycle length to discover the days you’re most fertile
          and increase your chances of conception.{' '}
        </p>
      </div>

      <div>
        <h3>Can My Due Date Change? </h3>
        <p>
          Yes, it’s possible your due delivery date may change. But it’s not a
          reason to worry as it can happen due to several reasons like irregular
          periods or error in early ultrasound dating.{' '}
        </p>
      </div>

      <div>
        <h3>How Likely Am I To Give Birth on My Due Date? </h3>
        <p>
          Based on research, around five percent of women give birth on the
          exact date as they are due for delivery. Most babies usually arrive
          within two weeks on either side of the due date.{' '}
        </p>
      </div>

      <div>
        <h3>How Soon Can I Take a Pregnancy Test? </h3>
        <p>
          You can{' '}
          <AnchorCoverTag
            url="/article/when-is-the-right-time-to-use-the-pregnancy-test-kit-166906"
            {...commonProps}
          >
            take the pregnancy test
          </AnchorCoverTag>{' '}
          take the pregnancy test either after the first day of the last missed
          period or after a period of 21 days from the date of unprotected sex
          preferably in the morning, with the first urine stream of the
          day.{' '}
        </p>
      </div>
      <YotubrVideoFrame src="https://www.youtube.com/watch?v=oaGEehp8JV4" />
    </DueDateContent>
  );
}
